import React, { useRef, useState } from "react";
import { Sparkles, Send, Loader2, X, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * AiAssist — reusable, schema-driven AI Form Assistant.
 *
 * Drop it into ANY form: pass the field `schema`, the current `values`, and an
 * `onFill(values)` callback. It talks to the platform-wide /ai/form-fill engine,
 * extracts values from natural language, asks only for what's missing, never
 * guesses, and NEVER submits — the user always reviews the filled form.
 *
 * Props:
 *   schema:   [{ name, label?, type?, required?, options?, description? }]
 *   values:   current form values object
 *   onFill:   (mergedValues) => void   // merge AI-extracted values into the form
 *   formTitle?: string
 *   endpoint?: string  // "/ai/form-fill" (auth) | "/public/ai/form-fill" (public)
 *   testId?:  string   // data-testid prefix
 */
export const AiAssist = ({
  schema, values, onFill, formTitle = "", endpoint = "/ai/form-fill", testId = "ai-assist",
}) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [assistantMsg, setAssistantMsg] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [complete, setComplete] = useState(false);
  const sessionId = useRef(`form-${Math.random().toString(36).slice(2)}-${Date.now()}`);

  const run = async () => {
    const text = message.trim();
    if (!text) return;
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, {
        schema, values: values || {}, message: text,
        form_title: formTitle, session_id: sessionId.current,
      });
      onFill(data.values || {});
      setAssistantMsg(data.assistant_message || "");
      setFollowUp(data.follow_up_question || "");
      setComplete(!!data.complete);
      setMessage("");
      if (data.complete) {
        toast.success("Form filled — please review and submit.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${testId}-open`}
        className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 bg-gradient-to-r from-[#6D28D9] to-[#DB2777] text-white hover:opacity-90 transition-opacity"
      >
        <Sparkles className="w-3.5 h-3.5" /> AI Assist
      </button>
    );
  }

  return (
    <div
      className="border border-[#C7D2FE] bg-[#F5F3FF] p-3 space-y-2"
      data-testid={`${testId}-panel`}
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#5B21B6]">
          <Sparkles className="w-3.5 h-3.5" /> AI Form Assistant
        </span>
        <button type="button" onClick={() => setOpen(false)} data-testid={`${testId}-close`} className="text-[#5B21B6]/70 hover:text-[#5B21B6]">
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-[11px] text-[#6B21A8]">
        Describe the details in your own words — I'll fill the fields for you to review.
      </p>
      {(assistantMsg || followUp) && (
        <div className="text-xs text-[#3730A3] bg-white/70 border border-[#DDD6FE] px-2 py-1.5" data-testid={`${testId}-reply`}>
          {complete && <CheckCircle2 className="w-3.5 h-3.5 inline mr-1 text-green-600" />}
          {assistantMsg}
          {followUp && <span className="block mt-1 font-medium">{followUp}</span>}
        </div>
      )}
      <Textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) run(); }}
        placeholder={followUp || 'e.g. "Acme Logistics, billing@acme.co.uk, 14 day terms"'}
        rows={2}
        className="text-sm bg-white"
        data-testid={`${testId}-input`}
      />
      <div className="flex items-center gap-2">
        <Button
          type="button" onClick={run} disabled={busy || !message.trim()}
          className="h-8 text-xs bg-[#6D28D9] text-white hover:bg-[#5B21B6]"
          data-testid={`${testId}-send`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          <span className="ml-1">{busy ? "Thinking…" : "Fill fields"}</span>
        </Button>
        <span className="text-[10px] text-[#6B21A8]/70">⌘/Ctrl + Enter</span>
      </div>
    </div>
  );
};

export default AiAssist;
